import React from 'react'
import {makeOcStyles} from '@oc/styles'
import {Menu, MenuItem, MenuItemProps, PopoverOrigin} from '@mui/material'

const useMenuItemStyles = makeOcStyles<{open: boolean}>((theme, props) => ({
    root: {
        backgroundColor: props.open ? theme.palette.action.hover : 'rgba(0,0,0,0)'
    },
    children: {pointerEvents: 'auto'},
    menu: {pointerEvents: 'none'}
}))

export interface NestedMenuItemProps extends Omit<MenuItemProps, 'button'> {
    parentMenuOpen: boolean
    label?: React.ReactNode
    ContainerProps?: React.HTMLAttributes<HTMLElement> & React.RefAttributes<HTMLElement | null>
    button?: true | undefined
    anchorOrigin?: PopoverOrigin
    transformOrigin?: PopoverOrigin
}

// This component origins from material-ui-nested-menu-item library (https://www.npmjs.com/package/material-ui-nested-menu-item)
export function NestedMenuItem(props: NestedMenuItemProps) {
    const {
        parentMenuOpen,
        label,
        children,
        className,
        tabIndex: tabIndexProp,
        ContainerProps: ContainerPropsProp = {},
        anchorOrigin,
        transformOrigin,
        ...MenuItemProps
    } = props

    const {ref: containerRefProp, ...ContainerProps} = ContainerPropsProp

    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)

    const menuItemRef = React.useRef<HTMLLIElement>(null)
    const containerRef = React.useRef<HTMLDivElement>(null)
    React.useImperativeHandle(containerRefProp as React.Ref<HTMLElement | null>, () => containerRef.current)
    const menuContainerRef = React.useRef<HTMLDivElement>(null)

    const handleMouseEnter = React.useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            setAnchorEl(event.currentTarget)

            if (ContainerProps?.onMouseEnter) {
                ContainerProps.onMouseEnter(event)
            }
        },
        [ContainerProps]
    )

    const handleMouseLeave = React.useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            setAnchorEl(null)

            if (ContainerProps?.onMouseLeave) {
                ContainerProps.onMouseLeave(event)
            }
        },
        [ContainerProps]
    )

    const isSubmenuFocused = React.useCallback(() => {
        const active = containerRef.current?.ownerDocument?.activeElement
        for (const child of menuContainerRef.current?.children ?? []) {
            if (child === active) {
                return true
            }
        }
        return false
    }, [])

    const handleFocus = React.useCallback(
        (event: React.FocusEvent<HTMLElement>) => {
            if (event.target === containerRef.current) {
                setAnchorEl(event.currentTarget)
            }

            if (ContainerProps?.onFocus) {
                ContainerProps.onFocus(event)
            }
        },
        [ContainerProps]
    )

    const handleKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Escape') {
                return
            }

            if (isSubmenuFocused()) {
                event.stopPropagation()
            }

            const active = containerRef.current?.ownerDocument?.activeElement

            if (event.key === 'ArrowLeft' && isSubmenuFocused()) {
                containerRef.current?.focus()
            }

            if (event.key === 'ArrowRight' && event.target === containerRef.current && event.target === active) {
                const firstChild = menuContainerRef.current?.children[0] as HTMLElement | undefined
                firstChild?.focus()
            }
        },
        [isSubmenuFocused]
    )

    const open = !!anchorEl && parentMenuOpen
    const {classes, cx} = useMenuItemStyles({open})

    let tabIndex
    if (!props.disabled) {
        tabIndex = tabIndexProp !== undefined ? tabIndexProp : -1
    }

    return (
        <div
            {...ContainerProps}
            ref={containerRef}
            onFocus={handleFocus}
            tabIndex={tabIndex}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onKeyDown={handleKeyDown}
        >
            <MenuItem {...MenuItemProps} className={cx(classes.root, className)} ref={menuItemRef}>
                {label}
            </MenuItem>
            <Menu
                className={classes.menu}
                anchorEl={anchorEl}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
                open={open}
                autoFocus={false}
                disableAutoFocus
                disableEnforceFocus
                onClose={() => {
                    setAnchorEl(null)
                }}
            >
                <div ref={menuContainerRef} className={classes.children}>
                    {children}
                </div>
            </Menu>
        </div>
    )
}
